import {
  findNode,
  getAboveNode,
  getEditorString,
  getNodeLeaf,
  getNodeProps,
  getPluginType,
  InsertNodesOptions,
  isDefined,
  isExpanded,
  isType,
  PlateEditor,
  removeNodes,
  setNodes,
  UnwrapNodesOptions,
  Value,
  WrapNodesOptions,
} from '@udecode/plate-common';

import {
  ELEMENT_ANCHOR_LINK,
  ELEMENT_LINK,
  MyInterlinkElement,
} from '../../../plateTypes';
import { ELEMENT_INTERLINK } from '../createInterlinkPlugin';
import { CreateInterlinkNodeOptions } from '../utils';
import { insertInterlink } from './insertInterlink';
import { unwrapLink } from './unwrapLink';
import { upsertLinkText } from './upsertLinkText';
import { wrapInterlink } from './wrapInterlink';

export type UpsertInterlinkOptions<V extends Value = Value> =
  CreateInterlinkNodeOptions & {
    insertNodesOptions?: InsertNodesOptions<V>;
    unwrapNodesOptions?: UnwrapNodesOptions<V>;
    wrapNodesOptions?: WrapNodesOptions<V>;
  };

/**
 * If selection in a link or is not url:
 *
 * - Insert text with url, exit If selection is expanded or `update` in a link:
 * - Remove link node, get link text Then:
 * - Insert link node
 */
export const upsertInterlink = <V extends Value>(
  editor: PlateEditor<V>,
  {
    linkId,
    linkTitle,
    text,
    insertNodesOptions,
    langId,
  }: UpsertInterlinkOptions<V>,
): boolean | void => {
  const at = editor.selection;

  if (!at) return;

  const linkAbove = getAboveNode<MyInterlinkElement>(editor, {
    at,
    match: (node) => {
      return isType(editor, node, [
        ELEMENT_LINK,
        ELEMENT_ANCHOR_LINK,
        ELEMENT_INTERLINK,
      ]);
    },
  });

  // anchor and focus in link -> insert text
  if (isDefined(text) && text.length === 0) {
    text = linkTitle;
  }

  // edit the link url and/or target
  if (linkAbove) {
    if (
      linkAbove[0].type !== ELEMENT_INTERLINK ||
      linkId !== linkAbove[0]?.linkId
    ) {
      setNodes<MyInterlinkElement>(
        editor,
        { type: ELEMENT_INTERLINK, linkId, linkTitle },
        { at: linkAbove[1] },
      );
    }
    upsertLinkText(editor, { text });

    return true;
  }

  // selection contains at one edge edge or between the edges
  const linkEntry = findNode<MyInterlinkElement>(editor, {
    at,
    match: { type: getPluginType(editor, ELEMENT_INTERLINK) },
  });

  const [linkNode, linkPath] = linkEntry ?? [];

  let shouldReplaceText = false;

  if (linkPath && text?.length) {
    const linkText = getEditorString(editor, linkPath);

    if (text !== linkText) {
      shouldReplaceText = true;
    }
  }

  if (isExpanded(at)) {
    // anchor and focus in link
    if (linkAbove) {
      unwrapLink(editor, {
        at: linkAbove[1],
      });
    } else {
      unwrapLink(editor, {
        split: true,
      });
    }

    wrapInterlink(editor, {
      linkId,
      linkTitle,
      langId,
    });

    upsertLinkText(editor, { text });

    return true;
  }

  if (shouldReplaceText) {
    removeNodes(editor, {
      at: linkPath,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = getNodeProps(linkNode ?? ({} as any));

  const path = editor.selection?.focus.path;
  if (!path) return;

  // link text should have the focused leaf marks
  const leaf = getNodeLeaf(editor, path);

  insertInterlink(
    editor,
    {
      ...props,
      linkId,
      linkTitle,
      langId,
      children: [
        {
          ...leaf,
          text,
        },
      ],
    },
    insertNodesOptions,
  );
  return true;
};
